import { Order } from 'entities/orders/model/types';
import { FC, useEffect, useState } from 'react';
import { formatTime } from 'shared/lib/time';
import { Typography } from 'shared/ui/typography';
import timeIcon from 'assets/icons/time-icon.svg';
import timeIconBlack from 'assets/icons/time-icon-black.svg';
import timeIconWhite from 'assets/icons/time-icon-white.svg';
import { useOrderState } from 'entities/orders/lib/hooks/order/useOrderState';
import { Flex } from 'shared/ui/flex';
import { NotesTooltip, TimeWrapper } from './styles';
import { statusesMap } from 'entities/orders/lib/helpers/data/map';

export const OrderTime: FC<{ order: Order }> = ({ order }) => {
	const [time, setTime] = useState('');
	const { isVisitProgress, isVisitEnd, isVisitMissed, isCanceled } = useOrderState(order);

	const visitStart = order.appointment?.dt_visit_start
		? formatTime({ time: order.appointment.dt_visit_start })
		: null;
	const visitEnd = order.appointment?.dt_visit_end
		? formatTime({ time: order.appointment.dt_visit_end })
		: null;

	const startTime = order.dt_end ? formatTime({ time: order.dt_start }) : null;
	const endTime = order.dt_end ? formatTime({ time: order.dt_end }) : null;

	useEffect(() => {
		const startTime = formatTime({ time: order.dt_start });
		const endTime = formatTime({ time: order.dt_end });
		if (startTime && endTime) {
			setTime(`${startTime.time} - ${endTime.time}`);
		}
	}, [order]);

	if (isVisitProgress && visitStart && endTime) {
		return (
			<Flex $alignItems="center" $wrap="nowrap">
				<TimeWrapper>
					<Typography
						icon={{
							src: timeIconBlack,
							align: 'center',
							size: 'small',
						}}
						pointer={true}
					>
						{visitStart?.time}
					</Typography>
				</TimeWrapper>
				<span style={{ whiteSpace: 'nowrap' }}>&nbsp;-&nbsp;</span>
				<Typography pointer={true}>{endTime?.time}</Typography>
			</Flex>
		);
	}

	if (isVisitEnd && visitStart && visitEnd) {
		return (
			<TimeWrapper>
				<Flex $alignItems="center" $wrap="nowrap" $width="fit-content">
					<Typography
						icon={{
							src: timeIconBlack,
							align: 'center',
							size: 'small',
						}}
						pointer={true}
					>
						{visitStart?.time}
					</Typography>
					<span style={{ whiteSpace: 'nowrap' }}>&nbsp;-&nbsp;</span>
					<Typography pointer={true}>{visitEnd?.time}</Typography>
				</Flex>
			</TimeWrapper>
		);
	}

	if ((isVisitMissed || isCanceled) && startTime && endTime) {
		return (
			<TimeWrapper variant="danger">
				<NotesTooltip className="notes">
					{statusesMap.get(order.status)}

					<svg style={{ position: 'absolute', bottom: '-8px', right: '16px' }} width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M9.23205 12C8.46225 13.3333 6.53775 13.3333 5.76795 12L1.00481 3.75C0.23501 2.41667 1.19726 0.75 2.73686 0.75L12.2631 0.75C13.8027 0.75 14.765 2.41667 13.9952 3.75L9.23205 12Z" fill="#A1AAC6"/>
					</svg>
				</NotesTooltip>
				<Flex $alignItems="center" $wrap="nowrap" $width="fit-content">
					<Typography
						icon={{
							src: timeIconWhite,
							align: 'center',
							size: 'small',
						}}
						pointer={true}
						colorVariant="white"
					>
						{startTime?.time}
					</Typography>
					<span style={{ whiteSpace: 'nowrap', color: 'white' }}>&nbsp;-&nbsp;</span>
					<Typography pointer={true} colorVariant="white">{endTime?.time}</Typography>
				</Flex>
			</TimeWrapper>
		);
	}

	return (
		<Typography
			style={{ whiteSpace: 'nowrap' }}
			icon={{ src: timeIcon, align: 'center', size: 'small' }}
			pointer={true}
		>
			{time}
		</Typography>
	);
};
